import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useApi } from "@platform/api";
import { Link, paletteVar, Typography } from "@platform/design-system";

import { ConfirmDialog, UploadDocumentZone } from "../../../../components";
import {
  ACCEPTED_FILE_FORMATS,
  ACCEPTED_FILE_FORMATS_LABEL,
  MAX_MB_RFP_FILE_SIZE,
} from "../../../../constants";
import { useToastNotificationService } from "../../../../hooks";
import { downloadBinaryFile } from "../../../../utils";
import { DocumentTypePanel } from "../../components";
import { DownloadTypography } from "../../components/styledComponents";
import { RFP_ACTION_TYPES } from "../../constants";
import { RfpContext } from "../../contexts/rfpContext";

const { UPDATE_ADDITIONAL_DOCUMENTS, REMOVE_ADDITIONAL_DOCUMENTS } =
  RFP_ACTION_TYPES;

const AdditionalDocumentPanel = ({
  documents,
  uploadedFiles,
  onSelect,
  isOpen,
}) => {
  const { setRfp, handleSaveDraft, deleteRFPDocument } = useContext(RfpContext);
  const [isReadyToSave, setIsReadyToSave] = useState(false);
  const [files, setFiles] = useState([]);
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();
  useEffect(() => {
    if (documents) {
      setFiles(documents);
    }
  }, [documents]);

  const handleRemoveFile = async (file) => {
    // A document with a row is re-derived from that row on the next load, so
    // dropping it from the draft alone would bring it straight back. Delete the
    // row first and leave the draft untouched if that fails — deleteRFPDocument
    // has already told the user, and the document really is still there.
    if (file.id != null) {
      const isDeleted = await deleteRFPDocument({
        id: file.id,
        name: file.name,
        fileName: file.name,
      });

      if (!isDeleted) {
        return;
      }
    }

    setRfp({
      type: REMOVE_ADDITIONAL_DOCUMENTS,
      value: file,
    });
    setIsReadyToSave(true);
  };

  const handleUploadFiles = (newFiles) => {
    setRfp({
      type: UPDATE_ADDITIONAL_DOCUMENTS,
      value: newFiles,
    });
    setIsReadyToSave(true);
  };

  useEffect(() => {
    if (isReadyToSave) {
      setIsReadyToSave(false);
      // isUpdatingRfp clears the document-uploading overlay that a preceding
      // deleteRFPDocument leaves raised on success.
      handleSaveDraft(false, true);
    }
  }, [isReadyToSave, handleSaveDraft]);
  const handleDownload = async (file) => {
    try {
      if (file instanceof File) {
        downloadBinaryFile(file, file.name);
      } else {
        const { data } = await axiosInstance.post(
          `brokerportal/v1/getRfpFile`,
          {
            downloadLink: file.downloadLink,
          },
          {
            responseType: "blob",
          },
        );

        downloadBinaryFile(data, file.name);
      }
    } catch {
      createToastNotification({
        severity: "error",
        message: `Unable to download file ${file.name}. Please try again.`,
        title: "Couldn't download file",
      });
    }
  };

  return (
    <DocumentTypePanel
      isOpen={isOpen}
      isRequired={false}
      title={"Additional Documents"}
      onClick={onSelect}
    >
      <Box>
        <UploadDocumentZone
          files={files}
          handleUploadFiles={(_files) => handleUploadFiles(_files)}
          maxSize={MAX_MB_RFP_FILE_SIZE}
          acceptedFormatFiles={ACCEPTED_FILE_FORMATS}
          allowedFormatLabel={ACCEPTED_FILE_FORMATS_LABEL}
          isMultiple={true}
          isAllowToManage={true}
          renderFiles={false}
          uploadedFiles={uploadedFiles}
          documentTypeName="Additional"
          isAdditionalDocument={true}
        />
      </Box>

      <CustomBox display="flex" flexDirection="column" gap={2}>
        <Box border={"1px solid rgba(224, 224, 224, 1)"}>
          <Table>
            <TableHead
              sx={{
                background: paletteVar("surfaces-primary", "#EFF8FB"),
              }}
            >
              <TableCell>
                <Typography variant="body-medium">
                  <strong>Uploaded Documents</strong>
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body-medium">
                  <strong></strong>
                </Typography>
              </TableCell>
            </TableHead>
            <TableBody>
              {files.length ? (
                files.map((item) => (
                  <DocumentFile
                    key={item.name}
                    file={item}
                    handleRemoveFile={handleRemoveFile}
                    handleDownload={handleDownload}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell>
                    <Typography variant="body-medium" color="textSecondary">
                      <em>No uploaded documents</em>
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </CustomBox>
    </DocumentTypePanel>
  );
};

export const CustomBox = styled(Box)(({ theme }) => ({
  boxShadow: "none",
  marginTop: "20px",
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.brandSupporting.main,
  },
  "&::-webkit-scrollbar-track": {
    background: paletteVar("surfaces-neutral", "#f2f2f2"),
  },
}));

const DocumentFile = ({ file, handleRemoveFile, handleDownload }) => {
  useTheme();
  const [openConfirm, setOpenConfirm] = useState(false);

  const onRemove = () => {
    handleRemoveFile(file);
    setOpenConfirm(false);
  };

  return (
    <TableRow id={file.name}>
      <TableCell sx={{ maxWidth: "300px" }}>
        <DownloadTypography onClick={() => handleDownload(file)}>
          {file.name}
        </DownloadTypography>
      </TableCell>
      <TableCell sx={{ textAlign: "right" }}>
        <Link
          component="button"
          onClick={() => setOpenConfirm(true)}
          underline="none"
        >
          <DeleteOutlineIcon />
        </Link>
      </TableCell>
      {openConfirm && (
        <ConfirmDialog
          title="Remove Document"
          message={
            <Typography>
              Are you sure you want to remove <strong>{file.name}</strong>?
            </Typography>
          }
          isOpen={openConfirm}
          closeBtnMsg="NO, CANCEL"
          onClose={() => setOpenConfirm(false)}
          confirmBtnMsg={"YES, REMOVE DOCUMENT"}
          onConfirm={onRemove}
        />
      )}
    </TableRow>
  );
};

export default AdditionalDocumentPanel;
